import { TextInput, Select, Button, Group, Stack, Paper, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useNavigate, useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useCompany, useCreateCompany, useUpdateCompany } from '@/hooks/useCompanies';
import { useEffect } from 'react';
import type { Company } from '@/types/company';

const legalForms = [
  'GmbH',
  'UG (haftungsbeschränkt)',
  'AG',
  'OHG',
  'KG',
  'GmbH & Co. KG',
  'Einzelunternehmen',
  'GbR',
  'e.K.',
];

type FormValues = Omit<Company, 'id' | 'createdAt' | 'updatedAt' | 'source' | 'industry' | 'contactPreference' | 'demoMessage' | 'contactFirstName' | 'contactLastName'>;

export function CompanyForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { data: existing } = useCompany(id ?? '');
  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany();

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
      legalForm: '',
      billingAddress: { street: '', houseNumber: '', postalCode: '', city: '', country: 'Deutschland' },
      phone: '',
      website: '',
      email: '',
      status: 'pending',
    },
    validate: {
      name: (v) => (v.trim() ? null : 'Name ist erforderlich'),
      legalForm: (v) => (v ? null : 'Gesellschaftsform ist erforderlich'),
      email: (v) => (/^\S+@\S+$/.test(v) ? null : 'Gültige E-Mail erforderlich'),
      billingAddress: {
        street: (v) => (v.trim() ? null : 'Straße ist erforderlich'),
        postalCode: (v) => (v.trim() ? null : 'PLZ ist erforderlich'),
        city: (v) => (v.trim() ? null : 'Stadt ist erforderlich'),
      },
    },
  });

  useEffect(() => {
    if (existing && isEdit) {
      form.setValues({
        name: existing.name,
        legalForm: existing.legalForm,
        billingAddress: existing.billingAddress,
        phone: existing.phone,
        website: existing.website,
        email: existing.email,
        status: existing.status,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing, isEdit]);

  const handleSubmit = (values: FormValues) => {
    if (isEdit && id) {
      updateCompany.mutate(
        { id, data: values },
        {
          onSuccess: () => {
            notifications.show({ title: 'Gespeichert', message: 'Unternehmen aktualisiert.', color: 'green' });
            navigate(`/companies/${id}`);
          },
        },
      );
    } else {
      createCompany.mutate(values, {
        onSuccess: (company) => {
          notifications.show({ title: 'Erstellt', message: 'Unternehmen angelegt.', color: 'green' });
          navigate(`/companies/${company.id}`);
        },
      });
    }
  };

  return (
    <Stack gap="md">
      <Title order={2}>{isEdit ? 'Unternehmen bearbeiten' : 'Neues Unternehmen'}</Title>
      <Paper withBorder p="md" radius="md">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="sm">
            <Group grow>
              <TextInput label="Name" required {...form.getInputProps('name')} />
              <Select
                label="Gesellschaftsform"
                required
                data={legalForms}
                {...form.getInputProps('legalForm')}
              />
            </Group>

            <TextInput label="E-Mail" required {...form.getInputProps('email')} />
            <Group grow>
              <TextInput label="Telefon" {...form.getInputProps('phone')} />
              <TextInput label="Website" {...form.getInputProps('website')} />
            </Group>

            <Title order={5} mt="sm">Rechnungsadresse</Title>
            <Group grow>
              <TextInput label="Straße" required {...form.getInputProps('billingAddress.street')} />
              <TextInput label="Hausnr." w={100} {...form.getInputProps('billingAddress.houseNumber')} />
            </Group>
            <Group grow>
              <TextInput label="PLZ" required {...form.getInputProps('billingAddress.postalCode')} />
              <TextInput label="Stadt" required {...form.getInputProps('billingAddress.city')} />
            </Group>
            <TextInput label="Land" {...form.getInputProps('billingAddress.country')} />

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={() => navigate(-1)}>
                Abbrechen
              </Button>
              <Button
                type="submit"
                loading={createCompany.isPending || updateCompany.isPending}
              >
                {isEdit ? 'Speichern' : 'Anlegen'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    </Stack>
  );
}
